import React, { useEffect, useState } from "react";
import Layout from "../../layouts/Layout";
import {
  Box,
  Typography,
  TextField,
  Button,
  InputLabel,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import axios from "axios";
import {
  ConfirmationSnackbar,
  ErrorSnackbar,
} from "../../Components/Common/CustomSnackbar";
import axiosInstance from "../../utils/config/config";
import { useLocation } from "react-router-dom";
import { BASE_URL } from "../../utils/config/config";

const SubmitRequest = () => {
  const location = useLocation();
  const content = location?.state?.content;
  const [message, setMessage] = useState("");
  const [fileUrls, setFileUrls] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [loading, setLoading] = useState(false);
  const [errorState, setErrorState] = useState({ error: false, message: "" });
  const [success, setSuccess] = useState(false);

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    setLoading(true);

    try {
      const uploadedUrls = [];

      for (const file of files) {
        const formData = new FormData();
        formData.append("file", file);

        const response = await axios.post(`${BASE_URL}/aws/upload`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        uploadedUrls.push(response.data.fileUrl);
      }

      setFileUrls((prev) => [...prev, ...uploadedUrls]);
    } catch (error) {
      console.error("File upload failed:", error);
      setErrorState({ error: true, message: "File upload failed." });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setLoading(true);

    try {
      if (!message.trim()) {
        setErrorState({
          error: true,
          message: "Message cannot be empty",
        });
        setSubmitting(false);
        setLoading(false);
        return;
      }

      const payload = {
        message,
        attachments: fileUrls,
      };

      if (content?.id) {
        payload.contentId = content.id;
      }

      await axiosInstance.post("/user/submitRequest", payload);

      setMessage("");
      setFileUrls([]);
      setSuccess(true);
    } catch (err) {
      console.error(err);
      setErrorState({
        error: true,
        message: "There was an error submitting your request.",
      });
    } finally {
      setSubmitting(false);
      setLoading(false);
    }
  };

  const getFileNameFromUrl = (url) => {
    return decodeURIComponent(url.split("/").pop());
  };

  return (
    <Layout>
      <ErrorSnackbar
        error={errorState.error}
        errorMsg={errorState.message}
        handleCloseError={() => setErrorState({ error: false, message: "" })}
      />
      <ConfirmationSnackbar
        snackbarState={success}
        handleSnackbarClose={() => setSuccess(false)}
        text={"Ticket raised"}
      />
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          backgroundColor: "#f9f9f9",
          minHeight: "40vh",
        }}
      >
        <Box
          sx={{
            backgroundColor: "#FC0108",
            color: "white",
            flex: 1,
            padding: "60px 40px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "left",
          }}
        >
          <Box maxWidth="sm">
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              Submit a Request
            </Typography>
            <Typography>
              If you have questions, need help, or would like to report an
              issue, please don’t hesitate to submit a request. Our support team
              is here to assist you and ensure your experience on OnlyHands is
              smooth and enjoyable. Fill out the form with as much detail as
              possible, and we’ll get back to you promptly.
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box sx={{ width: "100%", padding: "32px", borderRadius: "12px" }}>
            <form onSubmit={handleSubmit}>
              {content && (
                <Box mb={2}>
                  <video
                    src={content.video}
                    width="20%"
                    height="auto"
                    controls={false}
                    muted
                    playsInline
                    loop
                    style={{ borderRadius: "8px" }}
                  />
                </Box>
              )}

              <TextField
                label="Your Message"
                multiline
                rows={5}
                fullWidth
                required
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                sx={{ marginBottom: 3 }}
              />

              <InputLabel sx={{ marginBottom: 1 }}>
                Attachments (optional)
              </InputLabel>
              <input
                type="file"
                multiple
                onChange={handleFileUpload}
                style={{ marginBottom: 24 }}
              />

              {fileUrls.length > 0 && (
                <Box mb={2}>
                  <Typography fontWeight="bold" mb={1}>
                    Uploaded Files:
                  </Typography>
                  {fileUrls.map((url, index) => (
                    <Typography variant="body2" key={index}>
                      {getFileNameFromUrl(url)}
                    </Typography>
                  ))}
                </Box>
              )}

              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={submitting || loading}
                sx={{ padding: "10px", fontWeight: "bold" }}
              >
                {submitting
                  ? "Submitting..."
                  : loading
                  ? "Uploading..."
                  : "Submit Request"}
              </Button>
            </form>
          </Box>
        </Box>
      </Box>
    </Layout>
  );
};

export default SubmitRequest;
